/* eslint-disable no-useless-catch */
import {isApiError, ApigwResponse} from '@shared/helpers/api'

import {createAxiosInstance} from './axios'

const memoization: {
    [key: string]: ReturnType<typeof createAxiosInstance>
} = {}

/**
 * baseURL 을 기본으로 갖는 api 를 반환합니다.
 **/
const getApi = (baseURL: string, {cacheTTL}: {cacheTTL?: number} = {}): ReturnType<typeof createAxiosInstance> => {
    // cacheTTL 값이 다르면 따로 caching한다
    const memoizationKey = `${baseURL}${cacheTTL ? `|cacheTTL:${cacheTTL}` : ''}`
    if (!memoization[memoizationKey]) {
        memoization[memoizationKey] = createAxiosInstance({
            baseURL,
            cacheTTL,
        })
    }
    return memoization[memoizationKey]
}

export async function createWrapper<T>(apiPromise: Promise<{data: {result: T}}>): Promise<T> {
    try {
        const {
            data: {result},
        } = await apiPromise
        return result
    } catch (e) {
        throw e
    }
}

function isNoLoginInfo(e: unknown): boolean {
    try {
        const error = e as Error
        const {data} = JSON.parse(error.message) as {
            data: {
                code: string
                message: string
                result: {
                    status: string
                }
            }
            status: number
        }
        return data.code === ApigwResponse.NO_LOGIN_INFO
    } catch {
        return false
    }
}

/**
 * @deprecated
 * - createApiAsyncThunk 적용시 불필요합니다.
 */
export async function asyncActionWrapper<T>(
    apiPromise: () => Promise<T>,
    {
        rejectWithValue,
        noLoginInfo,
        delay,
    }: {
        rejectWithValue: AnyFunction
        noLoginInfo?: T
        delay?: number
    },
) {
    try {
        if (delay) {
            await new Promise((resolve) => setTimeout(resolve, delay))
        }

        const result = await apiPromise()

        return result
    } catch (e) {
        if (noLoginInfo && isNoLoginInfo(e)) {
            return noLoginInfo
        }
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
}

export default getApi

export * from './utils'

export {createApiAsyncThunk} from './redux'

export {
    isApiAsyncThunkAuthError,
    isApiAsyncThunkAuthRejected,
    isApiAsyncThunkFailPayload,
    isApiAsyncThunkFailRejected,
    isApiAsyncThunkServerPayload,
    isApiAsyncThunkServerRejected,
    isApiAsyncThunkNetworkPayload,
    isApiAsyncThunkNetworkRejected,
    isApiAsyncThunkTimeoutPayload,
    isApiAsyncThunkTimeoutRejected,
    isApiAsyncThunkMaintenancePayload,
    isApiAsyncThunkMaintenanceRejected,
    isApiAsyncThunkUnexpectedPayload,
    isApiAsyncThunkUnexpectedRejected,
    isApiAsyncThunkUnexpectedGatewayError,
    getServerResponseResult,
} from './typeGuard'

export type {
    ApiAsyncThunkAuthError,
    ApiAsyncThunkFailError,
    ApiAsyncThunkServerError,
    ApiAsyncThunkNetworkError,
    ApiAsyncThunkTimeoutError,
    ApiAsyncThunkMaintenanceError,
    ApiAsyncThunkUnexpectedError,
    ApiAsyncThunkAbortError,
} from './interfaces/error'
